import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Pencil, Ban, Plus, Save, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAdminRegulatoryDeadlines,
  useCancelRegulatoryDeadline,
  useUpdateRegulatoryDeadline,
  type AdminRegulatoryDeadline,
  type RegulatoryDeadlineStatus,
} from '@/hooks/useAdminRegulatoryDeadlines'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/utils'

// KAN-421 — `api.ts` is a shared helper: it throws ApiError carrying the raw,
// UNPARSED response body as `message`. Rather than change its semantics for
// every consumer in the app, decode our { error, message } envelope here, at
// the call site. Returns null whenever a human-readable string can't be
// recovered, so the caller falls back instead of rendering a JSON blob.
function parseServerMessage(body: string): string | null {
  const raw = body?.trim()
  if (!raw || !raw.startsWith('{')) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const { message } = parsed as { message?: unknown }
      if (typeof message === 'string' && message.trim()) return message.trim()
    }
  } catch {
    // Not JSON at all (api.ts falls back to response.statusText, and proxies can
    // return HTML). Nothing trustworthy to show — the caller falls back.
  }
  return null
}

// A 409 body should always carry `message`, but the API has at least one 409
// that ships only an `error` code, so a conflict-specific fallback is required.
const CONFLICT_FALLBACK = 'This deadline was modified by another editor. Refresh and try again.'

function errorNotice(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // `message` here is a response body, not prose — only trust what we decode.
    const serverMessage = parseServerMessage(err.message)
    if (serverMessage) return serverMessage
    return err.status === 409 ? CONFLICT_FALLBACK : fallback
  }
  // A non-ApiError (network failure, thrown TypeError) has genuine prose.
  if (err instanceof Error && err.message.trim()) return err.message.trim()
  return fallback
}

const STATUS_VARIANT: Record<RegulatoryDeadlineStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  upcoming: 'default',
  passed: 'secondary',
  cancelled: 'destructive',
}

const STATUS_OPTIONS: RegulatoryDeadlineStatus[] = ['upcoming', 'passed', 'cancelled']

function kindLabel(kind: string): string {
  return kind
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

type EditDraft = {
  description: string
  source_url: string
  status: RegulatoryDeadlineStatus
  expected_version: number
}

export function AdminRegulatoryDeadlinesListPage() {
  const { data, isLoading, isError, refetch } = useAdminRegulatoryDeadlines()
  const updateDeadline = useUpdateRegulatoryDeadline()
  const cancelDeadline = useCancelRegulatoryDeadline()
  const deadlines = data?.deadlines ?? []

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<EditDraft>({
    description: '',
    source_url: '',
    status: 'upcoming',
    expected_version: 1,
  })
  const [error, setError] = useState<string | null>(null)

  const startEditing = (deadline: AdminRegulatoryDeadline) => {
    setError(null)
    setEditingId(deadline.id)
    setDraft({
      description: deadline.description ?? '',
      source_url: deadline.source_url,
      status: deadline.status,
      expected_version: deadline.version,
    })
  }

  const cancelEditing = () => {
    setEditingId(null)
    setError(null)
  }

  const saveEditing = async (deadline: AdminRegulatoryDeadline) => {
    setError(null)
    try {
      await updateDeadline.mutateAsync({
        id: deadline.id,
        payload: {
          description: draft.description.trim() ? draft.description.trim() : null,
          source_url: draft.source_url.trim(),
          status: draft.status,
          expected_version: draft.expected_version,
        },
      })
      setEditingId(null)
    } catch (err) {
      setError(errorNotice(err, 'Could not update deadline'))
    }
  }

  const handleCancelDeadline = async (deadline: AdminRegulatoryDeadline) => {
    setError(null)
    try {
      // KAN-421 — send the version this row was rendered from so a cancel racing
      // another editor's change is rejected with 409 rather than silently
      // overwriting it.
      await cancelDeadline.mutateAsync({
        id: deadline.id,
        expected_version: deadline.version,
      })
    } catch (err) {
      // The hook refetches on 409 so the row picks up the current version; the
      // operator must still SEE the rejection, so the notice is in addition to
      // the refetch, never instead of it.
      setError(errorNotice(err, 'Could not cancel deadline'))
    }
  }

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Regulatory deadlines</h1>
            <p className="text-sm text-muted-foreground">
              Scraped and manually-entered deadlines. Edit description, source, or status; identity
              fields (label, due date) are immutable — cancel and recreate to change them.
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to="/admin/regulatory-deadlines/new">
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            New Deadline
          </Link>
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Failed to load regulatory deadlines"
          message="We couldn't reach the admin regulatory deadlines API. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : deadlines.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No regulatory deadlines yet"
          description="Run the deadline scraper or add one manually to populate this list."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-semibold">Framework</th>
                <th className="p-3 font-semibold">Kind</th>
                <th className="p-3 font-semibold">Due date</th>
                <th className="p-3 font-semibold">Label</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deadlines.map((deadline) => {
                const isEditing = editingId === deadline.id
                return (
                  <tr key={deadline.id} className="border-b align-top last:border-b-0 hover:bg-muted/20">
                    <td className="p-3">
                      <div className="font-medium">{deadline.framework_name ?? '—'}</div>
                      {deadline.framework_slug && (
                        <div className="text-xs text-muted-foreground">{deadline.framework_slug}</div>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{kindLabel(deadline.kind)}</td>
                    <td className="p-3 text-muted-foreground">{formatDate(deadline.due_date)}</td>
                    <td className="p-3">
                      <div className="font-medium">{deadline.label}</div>
                      {isEditing ? (
                        <div className="mt-2 flex flex-col gap-2">
                          <label className="text-xs font-medium text-muted-foreground" htmlFor={`description-${deadline.id}`}>
                            Description
                          </label>
                          <textarea
                            id={`description-${deadline.id}`}
                            value={draft.description}
                            onChange={(event) => setDraft((d) => ({ ...d, description: event.target.value }))}
                            className="min-h-16 rounded-md border bg-background px-2 py-1 text-sm"
                          />
                          <label className="text-xs font-medium text-muted-foreground" htmlFor={`source-${deadline.id}`}>
                            Source URL
                          </label>
                          <input
                            id={`source-${deadline.id}`}
                            type="url"
                            value={draft.source_url}
                            onChange={(event) => setDraft((d) => ({ ...d, source_url: event.target.value }))}
                            className="h-8 rounded-md border bg-background px-2 text-sm"
                          />
                        </div>
                      ) : (
                        deadline.description && (
                          <div className="text-xs text-muted-foreground">{deadline.description}</div>
                        )
                      )}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <select
                          aria-label={`Status for ${deadline.label}`}
                          value={draft.status}
                          onChange={(event) =>
                            setDraft((d) => ({ ...d, status: event.target.value as RegulatoryDeadlineStatus }))
                          }
                          className="h-8 rounded-md border bg-background px-2 text-sm"
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge variant={STATUS_VARIANT[deadline.status] ?? 'outline'}>
                          {deadline.status.charAt(0).toUpperCase() + deadline.status.slice(1)}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => saveEditing(deadline)}
                              disabled={updateDeadline.isPending}
                            >
                              <Save className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Save
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={cancelEditing}>
                              <X className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Discard
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditing(deadline)}
                              aria-label={`Edit ${deadline.label}`}
                            >
                              <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelDeadline(deadline)}
                              disabled={cancelDeadline.isPending || deadline.status === 'cancelled'}
                              aria-label={`Cancel ${deadline.label}`}
                            >
                              <Ban className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Cancel
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
